/**
 * The persona lens ("Perspectives"): the contract shared by
 * `getPersonaLens` (services/persona-metrics.ts), the
 * `GET /properties/:id/historical-metrics/personas` route, the MCP
 * `get_persona_lens` tool and the dashboard.
 *
 * Every discovery prompt on a project with personas also runs once per
 * persona each day (runType "nightly_persona"): the unchanged question with
 * the persona's one-line context in front. The lens compares each persona,
 * or a group of personas sharing an attribute, with the BASELINE: the same
 * prompts and platforms asked with no persona, over the same window. Google
 * AI Overviews is excluded on both sides (a search query has no asker).
 *
 * The shapes mirror the approved lab (`personaLens` in
 * `apps/web/modules/design/lib/persona-fixtures.ts`) with the lab-only
 * fields dropped: no persona versioning (edit markers) and no intervals on
 * the "Who AI recommends to this persona" shares (Jim, design round 1).
 *
 * Pure types and constants: safe for `@repo/core/client`.
 */

import type { PlatformKey } from "../utils/funnel-breakdown";
import type { PersonaAskerType } from "../utils/persona-context";

/** Shared attributes the lens groups personas by ("persona" = one row each). */
export const PERSONA_LENS_GROUP_BYS = [
	"persona",
	"roleGroup",
	"segment",
	"companySize",
	"seniority",
] as const;
export type PersonaLensGroupBy = (typeof PERSONA_LENS_GROUP_BYS)[number];

export function isPersonaLensGroupBy(
	value: unknown,
): value is PersonaLensGroupBy {
	return (
		typeof value === "string" &&
		(PERSONA_LENS_GROUP_BYS as readonly string[]).includes(value)
	);
}

/** Group key for personas with no value for the grouping attribute. */
export const PERSONA_LENS_UNSET_GROUP = "__unset__";

/**
 * Group key for individuals (people asking for themselves) under an
 * organization attribute (company size, seniority): they have none, and
 * they are not "Not set" either. Under "roleGroup" an individual's key is
 * `${PERSONA_LENS_PEOPLE_GROUP}:${segment}` when it has a segment, so people
 * group by audience segment where organization personas group by role.
 */
export const PERSONA_LENS_PEOPLE_GROUP = "__people__";

/** The persona fields the lens groups by. */
export interface PersonaLensGroupFields {
	id: string;
	/** Missing or unknown = "professional". */
	askerType?: string | null;
	/** Null for an individual. */
	roleGroup?: string | null;
	segment?: string | null;
	companySize?: string | null;
	seniority?: string | null;
}

/**
 * The audience segment of a people group key
 * (`${PERSONA_LENS_PEOPLE_GROUP}:Seniors on Medicare` → "Seniors on
 * Medicare"); "" for the bare people key; null for any other key.
 */
export function personaLensPeopleGroupSegment(key: string): string | null {
	if (key === PERSONA_LENS_PEOPLE_GROUP) {
		return "";
	}
	return key.startsWith(`${PERSONA_LENS_PEOPLE_GROUP}:`)
		? key.slice(PERSONA_LENS_PEOPLE_GROUP.length + 1)
		: null;
}

/**
 * The group a persona falls in: its id ("persona"), else the attribute value
 * (PERSONA_LENS_UNSET_GROUP when the persona has none). Individuals have no
 * organization attributes: under "roleGroup" they group by audience segment
 * (a people key), under "companySize" and "seniority" they share
 * PERSONA_LENS_PEOPLE_GROUP. Shared by the lens and the recommendations
 * persona-group filter so both group identically.
 */
export function personaLensGroupKey(
	persona: PersonaLensGroupFields,
	groupBy: PersonaLensGroupBy,
): string {
	const individual = persona.askerType === "individual";
	switch (groupBy) {
		case "persona":
			return persona.id;
		case "roleGroup": {
			if (individual) {
				const segment = persona.segment?.trim();
				return segment
					? `${PERSONA_LENS_PEOPLE_GROUP}:${segment}`
					: PERSONA_LENS_PEOPLE_GROUP;
			}
			return persona.roleGroup || "other";
		}
		case "segment":
			return persona.segment?.trim() || PERSONA_LENS_UNSET_GROUP;
		case "companySize":
			return individual
				? PERSONA_LENS_PEOPLE_GROUP
				: persona.companySize || PERSONA_LENS_UNSET_GROUP;
		case "seniority":
			return individual
				? PERSONA_LENS_PEOPLE_GROUP
				: persona.seniority || PERSONA_LENS_UNSET_GROUP;
	}
}

export const PERSONA_LENS_WINDOW_DAYS = [7, 30, 90] as const;
export type PersonaLensWindowDays = (typeof PERSONA_LENS_WINDOW_DAYS)[number];
/** Persona samples are thin: the lens defaults to 30 days. */
export const PERSONA_LENS_DEFAULT_WINDOW_DAYS: PersonaLensWindowDays = 30;

/** Rows (and cells) with fewer answers than this are grayed out. */
export const PERSONA_LENS_THIN_N = 30;

/** Confidence level of every interval the lens returns. */
export const PERSONA_LENS_CONFIDENCE = 0.9;

/** Brands shown in each "Who AI recommends to this persona" panel before Other. */
export const PERSONA_LENS_SOR_TOP_N = 5;

/** Untracked brands added as lens columns after the tracked ones. */
export const PERSONA_LENS_UNTRACKED_COLUMNS = 2;

/** Key of the folded tail row in a SoR panel. */
export const PERSONA_LENS_OTHER_KEY = "__other__";

/** Options of `getPersonaLens`. */
export interface PersonaLensOptions {
	/** 7 | 30 | 90 days ending today (UTC dispatch days). Default 30. */
	windowDays?: PersonaLensWindowDays;
	/** One platform; null = every persona platform (never AI Overviews). */
	platform?: PlatformKey | null;
	/**
	 * One project: its prompts, and its current (non-archived) personas.
	 * A project of another property yields an empty lens.
	 */
	projectId?: string | null;
	/** Default "persona". */
	groupBy?: PersonaLensGroupBy;
	/** Include archived personas (default false; they are listed instead). */
	includeArchived?: boolean;
}

/** Rates in percent (0 to 100), unrounded; null when n = 0. */
export interface PersonaLensRates {
	/** Answers in the slice: every rate's denominator. */
	n: number;
	/** Answers naming the brand in any way. */
	mentionRate: number | null;
	topChoiceRate: number | null;
	/** Top choice + one of many. */
	recommendedRate: number | null;
	cautionedRate: number | null;
	/** Interval half-widths in percentage points (100 when n = 0). */
	ci: {
		mention: number;
		topChoice: number;
		recommended: number;
		cautioned: number;
	};
}

export interface PersonaLensCell extends PersonaLensRates {
	brandKey: string;
	/** Baseline over the same prompts and platforms (and window). */
	baseline: PersonaLensRates;
	/** Persona minus baseline, percentage points. */
	delta: {
		mention: number | null;
		topChoice: number | null;
		recommended: number | null;
		cautioned: number | null;
	};
	/** Interval half-width of each difference, percentage points. */
	deltaCi: {
		mention: number;
		topChoice: number;
		recommended: number;
		cautioned: number;
	};
	/** |delta| is larger than its interval (never true on a thin cell). */
	clear: {
		mention: boolean;
		topChoice: boolean;
		recommended: boolean;
		cautioned: boolean;
	};
	/** n < thinThreshold: gray the cell out. */
	thin: boolean;
	/**
	 * The brand is something the row's personas already have or use (their
	 * "already have" list): an answer restating it is not a recommendation.
	 * Field name kept for API compatibility.
	 */
	inBuyerStack: boolean;
}

/** A lens column. Tracked status is resolved at read time. */
export interface PersonaLensBrand {
	brandKey: string;
	name: string;
	isFocal: boolean;
	isTracked: boolean;
	competitorId: string | null;
}

/** One row of "Who AI recommends to this persona". Shares sum to 100. */
export interface PersonaLensSorRow {
	/** PERSONA_LENS_OTHER_KEY for the folded tail. */
	brandKey: string;
	name: string;
	isFocal: boolean;
	isTracked: boolean;
	isOther: boolean;
	/** Brands folded into Other (0 otherwise). */
	foldedCount: number;
	/** Recommendations (top choice + one of many; a top choice counts once). */
	points: number;
	/** Share of recommendation among this persona's answers. */
	sharePct: number;
	/** The same brand's share at baseline (same prompts and platforms). */
	baselineSharePct: number;
	/** Something the persona already has or uses (false for Other). */
	inBuyerStack: boolean;
}

/** A persona as a lens row lists it. */
export interface PersonaLensMember {
	id: string;
	/**
	 * Display label, disambiguated: an individual's title ("Retired
	 * homeowner"), a professional's role-first label ("CFO, early-stage
	 * startup").
	 */
	label: string;
	/** The user-added name, or null (most personas have none). */
	name: string | null;
	title: string | null;
	/**
	 * Who is asking: "individual" (for themselves, their household or someone
	 * they care for) or "professional" (on behalf of an organization).
	 */
	askerType: PersonaAskerType;
	/** An individual's one-sentence household; null for a professional. */
	household: string | null;
	/** The attributes the lens groups by (organization ones null for an individual). */
	roleGroup: string | null;
	segment: string | null;
	companySize: string | null;
	seniority: string | null;
	archived: boolean;
	/** On an ACTIVE project but no persona answer has landed yet. */
	pendingFirstRun: boolean;
	/** Not on any ACTIVE project, so it does not run. */
	unassigned: boolean;
}

export interface PersonaLensRow {
	/** Persona id, or the attribute value (PERSONA_LENS_UNSET_GROUP = none). */
	key: string;
	/**
	 * Persona rows: the display label. Group rows: the attribute's label
	 * ("Finance", "11 to 50 employees", the segment text, "People",
	 * "Not set"). Individuals grouped by role read as their segment.
	 */
	label: string;
	/**
	 * Persona rows: the user-added name if set, else the company context
	 * (professional) or region and segment (individual).
	 * Group rows: "2 personas".
	 */
	sublabel: string;
	personaIds: string[];
	personas: PersonaLensMember[];
	/** Persona answers in the slice. */
	n: number;
	/** Baseline answers over the row's prompts and platforms. */
	baselineN: number;
	thin: boolean;
	/** Every persona in the row is waiting for its first answer. */
	pending: boolean;
	/** Some but not all personas in the row are pending. */
	partlyPending: boolean;
	/** Union of what the row's personas already have or use, as entered. */
	techStack: string[];
	/** One cell per lens column (`brands`), keyed by brandKey. */
	cells: Record<string, PersonaLensCell>;
	/** "Who AI recommends to this persona": top brands + Other. */
	sor: PersonaLensSorRow[];
}

export interface PersonaLensResponse {
	groupBy: PersonaLensGroupBy;
	windowDays: PersonaLensWindowDays;
	/** Inclusive UTC dispatch days of the window (YYYY-MM-DD). */
	from: string;
	to: string;
	platform: PlatformKey | null;
	/** Platforms in the comparison (never AI Overviews). */
	platforms: PlatformKey[];
	excludedPlatforms: PlatformKey[];
	projectId: string | null;
	hasFocalSubject: boolean;
	focal: { brandKey: string; name: string } | null;
	/** Does the property have any (non-archived) persona at all? */
	hasPersonas: boolean;
	/** Lens columns: focal, tracked competitors, then the top untracked. */
	brands: PersonaLensBrand[];
	rows: PersonaLensRow[];
	/** Baseline over the union of every row's prompts and platforms. */
	baseline: {
		n: number;
		cells: Record<string, PersonaLensRates>;
		sor: PersonaLensSorRow[];
	};
	/** Archived personas left out (empty with includeArchived). */
	archivedExcluded: string[];
	thinThreshold: number;
	/** Interval confidence (0.9). */
	confidence: number;
}
